import { BSON, Credentials } from 'realm-web';

import {
  Appointment,
  CodeableConcept,
  Extension,
  Location,
  Organization,
  Patient,
  Practitioner,
  Reference,
  Schedule
} from 'fhir/r4';
import { Notify, Dialog } from 'quasar';
import {
  Calendar,
  CaptchaInfo,
  RealmError,
  APIError
} from 'src/models/models';
import { AppointmentInterface, StatusType } from 'src/store/appointments/state';
import { FunctionsTypes } from './functions.types';
import { useUser } from 'src/store/user';
import { PatientStateInterface } from 'src/store/patient/state';
import { ObjectId } from 'bson';

import { i18n } from 'src/boot/i18n';

import { Realm } from 'src/boot/realm';
import { useConfig } from 'src/boot/config';

import {useBookingService} from 'src/boot/axios'

import BookingError  from 'src/components/dialogs/BookingError.vue';

interface BookPayload {
  patientIdentifier: string;
  slots: string[];
  specialty: string;
  service: string;
  comment: string;
  'attention-type': string;
}

export interface SearchQueryRequest {
  district?: string[];
  specialty?: string[];
  p_identifier?: string[];
  pr_identifier?: string[];
  start: string;
  end: string;
  organization?: string[];
  hcs?: string[];
  mode?: string[];
  service?: string;
  skip?: number;
  limit?: number;
}

function UserNotLogged() {
  Notify.create({
    color: 'negative',
    message: 'Usuario no registrado'
  });
  return new Error('User not Logged');
}

function searchAPIError(err: RealmError) {
  let error = BSON.EJSON.parse(err.error) as APIError;
  if (typeof(error) === 'string') {
    error = BSON.EJSON.parse(error) as APIError;
  }
  if (error.status === 503 && error.detail.code === 'ENDPOINT_ERROR') {
    /* Notify.create({
      color: 'negative',
      message:
        error.detail.response_detail ||
        'Error en la agenda, pruebe con otra hora'
    }); */
    console.error(error.detail.response_detail ||
        'Error en la agenda, pruebe con otra hora');

  } else if (error.detail.code === 'FONASA_ERROR') {
    Notify.create({
      color: 'negative',
      message: error.detail.message
    });
  } else {
    Notify.create({
      color: 'negative',
      message: i18n.global.t(`errors.${error.detail.code}`)
    });
  }
  return error.detail;
}

export function realmUser() {
  const client = Realm().currentUser;
  if (client) return Realm().currentUser;
}

export function realmUserLoggedIn() {
  const client = Realm().currentUser;
  if (client) return client.isLoggedIn;
  return false;
}

export function realmlogIn(credentials: Credentials) {
  return Realm()
    .logIn(credentials)
    .catch((e: { errorCode: string }) => {
      if (['AuthError', 'InvalidPassword'].includes(e.errorCode)) {
        Notify.create({
          color: 'negative',
          message: 'Correo o contraseña incorrectos'
        });
      }
      throw e;
    });
}

export async function realmLogInLink(credentials: Credentials) {
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged();
  await client.linkCredentials(credentials);
  await client.refreshProfile();
  return client;
}

export function realmCallResetPassword(email: string) {
  return Realm()
    .emailPasswordAuth.callResetPasswordFunction({email, 'password': '123456'})
    .then(() => {
      Notify.create({
        color: 'primary',
        message: 'Se ha enviado un correo para restablecer la contraseña'
      });
    })
    .catch((err: { errorCode: string; error: string }) => {
      if (['UserNotFound'].includes(err.errorCode)) {
        Notify.create({
          color: 'negative',
          message: 'Correo no registrado'
        });
      } else {
        Notify.create({
          color: 'negative',
          message: err.error
        });
      }
      throw err;
    });
}
export function realmResetPassword(
  token: string,
  tokenId: string,
  password: string
) {
  return Realm()
    .emailPasswordAuth.resetPassword({token, tokenId, password})
    .then(() => {
      Notify.create({
        color: 'primary',
        message: 'Contraseña restablecida'
      });
    })
    .catch((err: { errorCode: string; error: string }) => {
      if (['UserpassTokenInvalid'].includes(err.errorCode)) {
        Notify.create({
          color: 'negative',
          message:
            'Link para reestablecer inválido o expirado, vuelva a iniciar el proceso'
        });
      } else {
        Notify.create({
          color: 'negative',
          message: err.error
        });
        throw err;
      }
    });
}

export function realmLogOut() {
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged();
  return client.logOut();
}

export function realmRegister(email: string, password: string) {
  return Realm().emailPasswordAuth.registerUser({email, password});
}

export function realmGetCaptcha() {
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged();
  return client.functions[FunctionsTypes.AUTH_GET_CAPTCHA]() as Promise<
    CaptchaInfo
  >;
}

export async function locationsList(payload: string): Promise<Location[]> {
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged();
  return client.functions[FunctionsTypes.LOCATIONS_LIST](payload) as Promise<
    Location[]
  >;
}

export async function specialtiesList(
  districts: string[],
  hcs: string[] = [],
  mode: string[] = []
): Promise<CodeableConcept[]> {
  if (!districts.length && !hcs.length) {
    return Promise.resolve([])
  }
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged();
  return client.functions[FunctionsTypes.SPECIALTIES_LIST](
    districts,
    hcs,
    mode
  ) as Promise<CodeableConcept[]>;
}

export async function searchSchedules(
  payload: SearchQueryRequest
): Promise<Schedule[]> {
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged();
  return (await client.functions[FunctionsTypes.SEARCH_SCHEDULES](
    payload
  )) as Schedule[];
}

export async function searchPractitioner(payload: {
  pr_identifier?: string;
  district?: string;
  specialty?: string;
  p_identifier?: string;
  practitioner?: string;
}): Promise<Practitioner[]> {
  const search_params = new URLSearchParams();
  if (payload.pr_identifier) {
    search_params.append('pr_identifier', payload.pr_identifier)
  }
  if (payload.specialty) {
    search_params.append('specialty', payload.specialty)
  }
  if(payload.district) {
    search_params.append('district', payload.district)
  }
  if (payload.p_identifier) {
    search_params.append('p_identifier', payload.p_identifier.toUpperCase())
  }
  if (payload.practitioner) {
    search_params.append('practitioner', payload.practitioner)
  }
  // return await Promise.resolve([] as Practitioner[])
  try {
    const bookingService = useBookingService()
    const resp = await bookingService.get('practitioners', {params: search_params});
    return resp.data as Practitioner[];
  } catch (error) {
    console.log(error)
    return [] as Practitioner[]
  }
}

export async function searchCalendar(payload: {
  district?: string[];
  specialty?: string[];
  month: number;
  year: number;
  mode?: string[];
  p_identifier?: string[];
  organization?: string[];
  hcs?: string[];
}) {
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged();
  return (await client.functions[FunctionsTypes.SEARCH_CALENDAR](
    payload
  )) as Calendar[];
}

export async function bookAppointment(payload: BookPayload) {
  const { patientIdentifier, slots, specialty, service, comment } = payload;
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged();
  return client.functions[FunctionsTypes.BOOK_APPOINTMENT](
    patientIdentifier,
    slots,
    specialty,
    service,
    comment,
    payload['attention-type']
  )
    .then((result: Appointment) => {
      Notify.create({
        color: 'positive',
        message: '¡Tu hora ha sido agendada!'
      });
      return result;
    })
    .catch((err: RealmError) => {
      Dialog.create({
        component: BookingError,
      })
      throw searchAPIError(err);
    });
}

export function accelerateAppointment(
  payload: BookPayload,
  apponitmentId: string
) {
  const { patientIdentifier, slots, specialty, service, comment } = payload;
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged();
  return client.functions[FunctionsTypes.BOOK_APPOINTMENT](
    patientIdentifier,
    slots,
    specialty,
    service,
    comment,
    payload['attention-type']
  ).then((result: Appointment) => {
    appointmentsUpdate(
      apponitmentId,
      patientIdentifier,
      'cancelled',
      false
    ).catch(() => {
      Notify.create({
        color: 'negative',
        message:
          'Se agendó la nueva hora pero falló la cancelación de la hora previa. Por favor cancela la hora manualmente.'
      });
    });
    Notify.create({
      color: 'positive',
      message: '¡Tu hora ha sido acelerada!'
    });
    return result;
  });
}

export async function organizationsList(payload: string[]) {
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged();
  return (await client.functions[FunctionsTypes.ORGANIZATIONS_LIST](
    payload
  )) as Organization[];
}

export async function getVerificationEmail(patient: string, email: string) {
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged();
  return (await client.functions[FunctionsTypes.AUTH_EMAIL_SEND](
    patient,
    email
  )) as void;
}

export async function editEmail(patient: string, email: string) {
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged();
  return (await client.functions[FunctionsTypes.AUTH_EMAIL_EDIT](
    patient,
    email
  )) as void;
}

export async function verifyEmail(
  patient: string,
  email: string,
  code: string
) {
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged();
  return client.functions[FunctionsTypes.AUTH_EMAIL_VERIFY](
    patient,
    email,
    code
  ).catch(err => {
    Notify.create({
      color: 'negative',
      message: 'Código de verificación no es válido'
    });
    throw err;
  });
}

export async function getScheduleSlot(schedule: string, slots: string[]) {
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged();
  return (await client.functions[FunctionsTypes.SEARCH_SCHEDULE_SLOT]({
    schedule,
    slots
  })) as Schedule;
}

export async function appointmentsRead(code: string, identifier: string) {
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged();
  return (await client.functions[FunctionsTypes.APPOINTMENTS_READ](
    code,
    identifier
  ).catch((realmError: { error: string }) => {
    if (realmError.error.includes('not Found')) {
      Notify.create({
        color: 'negative',
        message: 'RUT o Código de reserva no válido'
      });
    }
    throw realmError;
  })) as AppointmentInterface;
}

export async function appointmentsList() {
  const userStore = useUser();
  const client = Realm().currentUser;
  if (!userStore.isLoggedIn || !client) throw UserNotLogged();
  return (await client.functions[
    FunctionsTypes.APPOINTMENTS_LIST
  ]()) as AppointmentInterface[];
}

export async function appointmentsHistoryList() {
  const userStore = useUser();
  const client = Realm().currentUser;
  if (!userStore.isLoggedIn || !client) throw UserNotLogged();
  return (await client.functions[
    FunctionsTypes.APPOINTMENTS_HISTORY_LIST
  ]()) as AppointmentInterface[];
}

export function appointmentsUpdate(
  id: string,
  identifier: string,
  status: StatusType,
  showMsg = true
) {
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged();
  return client.functions[FunctionsTypes.APPOINTMENTS_UPDATE](
    id,
    identifier,
    status
  )
    .then(() => {
      if (showMsg) {
        if (status === 'cancelled') {
          Notify.create({
            color: 'positive',
            message: '¡Tu cita ha sido Anulada!'
          });
        } else if (status === 'confirmed') {
          Notify.create({
            color: 'positive',
            message: '¡Tu cita ha sido Confirmada!'
          });
        }
      }
    })
    .catch((err: RealmError) => {
      throw searchAPIError(err);
    });
}

export function authRegister(payload: Patient, code: string) {
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged();
  if (client.providerType !== 'anon-user') throw UserNotLogged();
  return client.functions[FunctionsTypes.AUTH_REGISTER](payload, code).catch(
    (err: RealmError) => {
      throw searchAPIError(err).code;
    }
  );
}

export function authVerify(email: string) {
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged()
  if (client.providerType !== 'anon-user') throw UserNotLogged();
  return client?.functions[FunctionsTypes.AUTH_REGISTER_VERIFY](email).catch(
    (err: RealmError) => {
      throw searchAPIError(err).code;
    }) as Promise<{code: string}>;
}

export function authVerifyIdentifier(identifier: string) {
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged()
  if (client.providerType !== 'anon-user') throw UserNotLogged();
  return client?.functions[FunctionsTypes.AUTH_VERIFY_IDENTIFIER](identifier).catch(
    (err: RealmError) => {
      throw searchAPIError(err).code;
    }) as Promise<boolean>;
}

export function authVerifyEmail(email: string) {
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged()
  if (client.providerType !== 'anon-user') throw UserNotLogged();
  return client?.functions[FunctionsTypes.AUTH_VERIFY_EMAIL](email).catch(
    (err: RealmError) => {
      const code = searchAPIError(err).code;
      throw code;
    }) as Promise<boolean>;
}

export function patientUpdate(payload: Patient) {
  const userStore = useUser();
  const client = Realm().currentUser;
  if (!userStore.isLoggedIn || !client) throw UserNotLogged();
  const { name, telecom, gender, birthDate, address, extension } = payload;
  if (!name || !telecom || !gender || !birthDate || !address || !extension)
    throw UserNotLogged();
  return client.functions[FunctionsTypes.PATIENT_UPDATE](payload) as Promise<
    void
  >;
}

export function refreshCustomData() {
  const userStore = useUser();
  const client = Realm().currentUser;
  if (!userStore.isLoggedIn || !client) throw UserNotLogged();
  return (client.refreshCustomData() as unknown) as Promise<
    PatientStateInterface
  >;
}

export function relatedPatientsList() {
  const userStore = useUser();
  const config = useConfig();
  const client = Realm().currentUser;
  if (!userStore.isLoggedIn || !client) throw UserNotLogged();
  const mongodb = client.mongoClient('mongodb-atlas');
  return mongodb
    .db(config.dbName)
    .collection('relatedPatients')
    .find({}) as Promise<Patient[]>;
}

export function relatedPatientsAdd(patient: Patient) {
  const userStore = useUser();
  const client = Realm().currentUser;
  if (!userStore.isLoggedIn || !client) throw UserNotLogged();
  return client.functions[FunctionsTypes.RELATED_ADD](patient) as Promise<{
    insertedId: ObjectId;
  }>;
}

export function relatedPatientsUpdate(patient: Patient) {
  const userStore = useUser();
  const client = Realm().currentUser;
  if (!userStore.isLoggedIn || !client) throw UserNotLogged();
  return client.functions[FunctionsTypes.RELATED_UPDATE](patient) as Promise<{
    modifiedCount: number;
  }>;
}

export function relatedPatientsRemove(patientId: string) {
  const userStore = useUser();
  const config = useConfig();
  const client = Realm().currentUser;
  if (!userStore.isLoggedIn || !client) throw UserNotLogged();
  const mongodb = client.mongoClient('mongodb-atlas');
  return mongodb
    .db(config.dbName)
    .collection('relatedPatients')
    .deleteOne({
      _id: new ObjectId(patientId)
    });
}

export function onDemandTeledoc() {
  const userStore = useUser();
  const client = Realm().currentUser;
  if (!userStore.isLoggedIn || !client) throw UserNotLogged();
  return client.functions[FunctionsTypes.ONDEMAND_TELEDOC]() as Promise<{
    url: string;
  }>;
}

export function favoriteAdd(favorite: Reference) {
  const userStore = useUser();
  const client = Realm().currentUser;
  if (!userStore.isLoggedIn || !client) throw UserNotLogged();
  return client.functions[FunctionsTypes.FAVORITE_ADD](favorite) as Promise<
    void
  >;
}

export function favoriteRemove(favorite: Reference) {
  const userStore = useUser();
  const client = Realm().currentUser;
  if (!userStore.isLoggedIn || !client) throw UserNotLogged();
  return client.functions[FunctionsTypes.FAVORITE_REMOVE](favorite) as Promise<
    void
  >;
}

export function configUpdate(payload: Extension[]) {
  const userStore = useUser();
  const client = Realm().currentUser;
  if (!userStore.isLoggedIn || !client) throw UserNotLogged();
  return client.functions[FunctionsTypes.PATIENT_CONFIG](payload) as Promise<
    void
  >;
}

export function practitionersList(payload: {
  query: string;
  searchCode: string;
}) {
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged();
  const { query, searchCode } = payload;
  return client.functions[FunctionsTypes.PRACTITIONERS_LIST](
    query,
    searchCode
  ).catch((err: RealmError) => {
    searchAPIError(err);
    throw err;
  }) as Promise<Reference[]>;
}


export function fonasaVoucherCreate (payload: {appId: string, docNum: string}) {
  /*
    appId: appointment id
    docNum: document number
  */
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged();
  const { appId, docNum } = payload;
  return client.functions[FunctionsTypes.FONASA_VOUCHER_CREATE](
    appId,
    docNum
  ).catch((err: RealmError) => {
    searchAPIError(err);
    throw err;
  }) as Promise<{identifier: {value:string}[],status: string, price: {copay: number, total: number}, valid_email: string}>;
}

export function fonasaVoucherCancel (payload: string) {
  /*
    payload: id encuentro médico
  */
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged();
  return client.functions[FunctionsTypes.FONASA_VOUCHER_CANCEL](
    payload
  ).catch((err: RealmError) => {
    searchAPIError(err);
    throw err;
  }) as Promise<void>;
}

export function fonasaVoucherGet (payload: string) {
  /*
    payload: id encuentro médico
  */
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged();
  return client.functions[FunctionsTypes.FONASA_VOUCHER_GET](
    payload
  ).catch((err: RealmError) => {
    searchAPIError(err);
    throw err;
  }) as Promise<string>;
}
export function fonasaVoucherList (id?: string) {
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged();
  return client.functions[FunctionsTypes.FONASA_VOUCHER_LIST](id
  ).catch((err: RealmError) => {
    searchAPIError(err);
    throw err;
  }) as Promise<{identifier: {value:string}[], status: string, price: {copay: number, total: number}, valid_email: string}[]>;
}

export function fonasaVoucherPay (payload: string) {
  /*
    payload: id encuentro médico
  */
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged();
  return client.functions[FunctionsTypes.FONASA_VOUCHER_PAY](
    payload
  ).catch((err: RealmError) => {
    searchAPIError(err);
    throw err;
  }) as Promise<string>;
}

export function benefits_assign(provider: string, identifier: string) {
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged();
  return client.functions[FunctionsTypes.BENEFITS_ASSIGN]({provider, identifier}).catch(
    (err: RealmError) => {
      throw searchAPIError(err);
    }) as Promise<{benefit: boolean}>;
}

export function benefits_function(provider: string, function_name: string) {
  const client = Realm().currentUser;
  if (!client) throw UserNotLogged();
  return client.functions[FunctionsTypes.BENEFITS_FUNCTION]({provider, function_name}).catch(
    (err: RealmError) => {
      throw searchAPIError(err);
    }) as Promise<{benefit: boolean}>;
}
